/**
 * A unified diff, as git printed it, rendered as a table.
 *
 * The patch text comes from the server (`GET …/patch`), which runs git's own
 * diff; nothing here computes a diff. This component only parses the hunk
 * headers to number the lines, and renders every line as text — never as
 * HTML — so a file containing markup is shown, not executed.
 */

import { cx } from '~/lib/cx';

import styles from './DiffView.module.css';

export interface DiffLine {
  kind: 'hunk' | 'context' | 'added' | 'removed' | 'meta';
  text: string;
  oldNumber?: number | undefined;
  newNumber?: number | undefined;
}

const HUNK = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

/** Parse git's unified diff output into numbered lines. Headers are dropped. */
export function parseUnifiedDiff(patch: string): DiffLine[] {
  const lines: DiffLine[] = [];
  let oldLine = 0;
  let newLine = 0;
  let inHunk = false;
  for (const raw of patch.split('\n')) {
    const hunk = HUNK.exec(raw);
    if (hunk) {
      oldLine = Number(hunk[1]);
      newLine = Number(hunk[2]);
      inHunk = true;
      lines.push({ kind: 'hunk', text: raw });
      continue;
    }
    if (!inHunk) continue; // diff --git, index, ---/+++ headers
    if (raw.startsWith('+')) {
      lines.push({ kind: 'added', text: raw.slice(1), newNumber: newLine++ });
    } else if (raw.startsWith('-')) {
      lines.push({ kind: 'removed', text: raw.slice(1), oldNumber: oldLine++ });
    } else if (raw.startsWith(' ')) {
      lines.push({
        kind: 'context',
        text: raw.slice(1),
        oldNumber: oldLine++,
        newNumber: newLine++,
      });
    } else if (raw.startsWith('\\')) {
      lines.push({ kind: 'meta', text: raw });
    }
  }
  return lines;
}

export interface DiffViewProps {
  patch: string;
  /** Names the table for assistive technology, usually the file path. */
  label: string;
  className?: string;
}

export function DiffView({ patch, label, className }: DiffViewProps) {
  const lines = parseUnifiedDiff(patch);
  if (lines.length === 0) {
    return <p className={styles.notice}>No textual changes (a mode change or an empty file).</p>;
  }
  return (
    <div className={cx(styles.scroll, className)}>
      <table className={styles.table} aria-label={label}>
        <tbody>
          {lines.map((line, index) => (
            <tr
              key={index}
              className={cx(
                line.kind === 'added' && styles.added,
                line.kind === 'removed' && styles.removed,
                (line.kind === 'hunk' || line.kind === 'meta') && styles.hunk,
              )}
            >
              <td className={styles.number}>{line.oldNumber ?? ''}</td>
              <td className={styles.number}>{line.newNumber ?? ''}</td>
              <td className={styles.marker} aria-hidden="true">
                {line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ''}
              </td>
              <td className={styles.text}>
                {line.kind === 'added' ? <span className={styles.srOnly}>added: </span> : null}
                {line.kind === 'removed' ? <span className={styles.srOnly}>removed: </span> : null}
                {line.text}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface CodeViewProps {
  content: string;
  label: string;
}

/** A text file with line numbers. Rendered as text, never as HTML. */
export function CodeView({ content, label }: CodeViewProps) {
  const lines = content.endsWith('\n') ? content.slice(0, -1).split('\n') : content.split('\n');
  return (
    <div className={styles.scroll}>
      <table className={styles.table} aria-label={label}>
        <tbody>
          {lines.map((line, index) => (
            <tr key={index}>
              <td className={styles.number}>{index + 1}</td>
              <td className={styles.text}>{line}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
